// "My Coaching" — for the agent and supervisor logins, always showing the
// logged-in person's OWN calls.
//
// Every call is machine-scored on seven parameters. This screen answers one
// question: which of those parameters are you weak in, and how do you fix each
// one? The weak list is decided server-side from the stored scores; the AI only
// supplies the remedy for each weak parameter, and every remedy cites the calls
// it was drawn from so it can be checked.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  GraduationCap, Loader2, Trophy, Target, ClipboardList,
  CheckCircle2, AlertTriangle, Info, ExternalLink, ChevronDown, HelpCircle, X,
  Wrench, MessageSquareQuote, Dumbbell, Ruler,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DateRangePicker } from '../components/ui/date-range-picker';

// Status colours. These encode a band (weak / moderate / strong), not an
// identity, and every use is paired with the band's word so nothing depends on
// colour alone.
const STATUS = {
  weak: { label: 'Weak', bar: '#D97706', chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  moderate: { label: 'OK', bar: '#0EA5E9', chip: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  strong: { label: 'Strong', bar: '#0D9488', chip: 'bg-teal-500/15 text-teal-300 border-teal-500/30' },
  unscored: { label: 'Not scored', bar: '#475569', chip: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
};

// What each scored parameter actually measures, in the agent's own terms. Taken
// from the scoring definitions the analysis prompt uses, so the explanation on
// screen matches what the score was actually given for.
const PARAMETER_MEANING = {
  greeting_quality: 'How you open the call — do you give your name and say you are calling from the hospital?',
  communication_clarity: 'Whether the patient was clear on the purpose of the call and on what happens next, with no confusion left unresolved.',
  grammar_score: 'How you form your sentences — complete, coherent and easy to follow when spoken.',
  professionalism_score: 'Whether you stayed courteous, respectful and professional in tone and choice of words.',
  empathy_score: 'Whether you acknowledged the patient\'s concern and showed you understood it before moving on.',
  solution_score: 'Whether you gave a concrete solution, next step or proper redirect — not just information.',
  closing_quality: 'How you end the call — confirming the patient understood, and closing politely.',
};

// No "My Best Calls" tab: a list ranked by overall score answered a question
// this screen does not ask. The best calls now live on the dashboard, and the
// calls that matter here — the ones where a WEAK parameter went right — are
// shown per parameter, inside the fix for it.
const TABS = [
  { key: 'plan', label: 'My Weak Areas', icon: Target },
  { key: 'practice', label: 'Calls To Fix', icon: ClipboardList },
];

// How each flagged parameter is described. A score that clears the pass mark is
// a relative weak spot, not a failure, and must never be labelled as one.
const WEAK_REASON = {
  below_pass_mark: {
    chip: 'Weak',
    chipClass: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    explain: (item, threshold) => `Below the ${threshold} / 10 pass mark — a real gap to close.`,
  },
  below_your_average: {
    chip: 'Focus area',
    chipClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    // "across all seven" was hardcoded, but personal_average is only averaged
    // over parameters that actually have a score. With one unscored parameter
    // the number is an average of six while the sentence claims seven, so the
    // count is passed in from the same set the server averaged.
    explain: (item, threshold, scoredCount) =>
      `Above the pass mark, but your weakest area — you average ${item.personal_average} across all ${scoredCount} of your scored parameters.`,
  },
  lowest_parameter: {
    chip: 'Focus area',
    chipClass: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    explain: () => 'Everything is solid — this is simply your lowest, so it is where the most room is left.',
  },
};

const weakReasonOf = (item) => WEAK_REASON[item?.weak_reason] || WEAK_REASON.below_pass_mark;

// Why a parameter is not being coached this period. Every parameter gets one of
// these, so none can quietly disappear from the screen — the case that forced
// this was a third-lowest parameter that was simply absent, with no explanation.
const NOT_COACHED_REASON = {
  no_evidence: (max) =>
    `Not coached: no call in this period explains its score well enough to build advice from (a call must run 30 seconds or more, score above 0 out of ${max}, and carry a written reason).`,
  not_lowest: () =>
    'Not coached: it is not among your lowest parameters, and only the lowest few are coached at a time.',
  unscored: () => 'Not coached: no call in this period was scored on it.',
};

const HELP_HIDDEN_KEY = 'vcm_coaching_help_hidden';

const toDateInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayInput = () => toDateInput(new Date());
const daysAgoInput = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};
const monthsAgoInput = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return toDateInput(d);
};

const decimal1 = (value) => (value === null || value === undefined ? '-' : Number(value).toFixed(1));

function scoreBadgeClass(score) {
  if (score === null || score === undefined) return 'bg-slate-500/15 text-slate-300';
  if (score >= 90) return 'score-excellent';
  if (score >= 75) return 'score-good';
  if (score >= 60) return 'score-review';
  return 'score-critical';
}

function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return '-';
  const mins = Math.floor(total / 60);
  const secs = Math.round(total % 60);
  return `${mins}m ${String(secs).padStart(2, '0')}s`;
}

function titleCase(value) {
  return String(value || '-').replace(/_/g, ' ');
}

const TINTS = {
  primary: 'bg-primary/15 text-primary',
  teal: 'bg-teal-500/15 text-teal-300',
  sky: 'bg-sky-500/15 text-sky-300',
  violet: 'bg-violet-500/15 text-violet-300',
  amber: 'bg-amber-500/15 text-amber-300',
};

function SectionBadge({ icon: Icon, tint = 'primary', size = 'md' }) {
  const box = size === 'sm' ? 'w-7 h-7 rounded-md' : 'w-9 h-9 rounded-lg';
  const glyph = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <span className={`${box} ${TINTS[tint] || TINTS.primary} flex items-center justify-center flex-shrink-0`}>
      <Icon className={glyph} />
    </span>
  );
}

function CountPill({ value }) {
  if (value === undefined || value === null) return null;
  return (
    <span className="px-2 py-0.5 rounded-full bg-white/5 border border-border text-[11px] tabular-nums text-muted-foreground flex-shrink-0">
      {value}
    </span>
  );
}

function Stat({ label, value, hint, accent }) {
  return (
    <div className="bg-card px-4 py-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold tabular-nums mt-0.5 ${accent || ''}`}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{hint}</p>}
    </div>
  );
}

function Panel({ children, className = '', ...props }) {
  return (
    <div className={`bg-card border border-border rounded-xl ${className}`} {...props}>
      {children}
    </div>
  );
}

function SectionHeading({ icon, tint, title, description, count }) {
  return (
    <div className="flex items-center gap-3 pb-3 mb-3 border-b border-border">
      <SectionBadge icon={icon} tint={tint} />
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-bold tracking-tight leading-tight">{title}</h2>
        {description && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>}
      </div>
      <CountPill value={count} />
    </div>
  );
}

function PanelHeader({ icon, tint, title, description, action }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 px-4 sm:px-5 py-4 border-b border-border">
      <div className="flex items-start gap-3 min-w-0">
        {icon && <SectionBadge icon={icon} tint={tint} />}
        <div className="min-w-0">
          <h3 className="text-base font-bold tracking-tight leading-tight">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function Collapsible({ icon, tint, title, subtitle, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
        aria-expanded={open}
      >
        <SectionBadge icon={icon} tint={tint} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        <CountPill value={count} />
        <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-4 border-t border-border">{children}</div>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="py-14 px-6 text-center">
      <Icon className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
      <p className="text-sm font-medium">{title}</p>
      {description && <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// A labelled block inside a card — the label is what tells you which of several
// similar-looking paragraphs you are reading, so it is set at full foreground
// weight. A dimmed sub heading over dimmed body text is two greys and no
// hierarchy: you cannot skim the card for the section you want.
function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-foreground mb-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-primary" />}
        {label}
      </p>
      {children}
    </div>
  );
}

function HowItWorks({ onHide, weakCount, threshold }) {
  const steps = [
    {
      title: 'Every call is scored',
      body: 'The AI scores each of your calls on seven things: greeting, clarity, grammar, professionalism, empathy, solution and closing.',
      note: 'Each one is scored out of 10.',
      tint: 'sky',
    },
    {
      title: 'Weak parameters are found',
      body: `Any parameter averaging below ${threshold} out of 10 across the dates you picked is marked weak.`,
      note: weakCount > 0 ? `${weakCount} of yours ${weakCount === 1 ? 'is' : 'are'} weak right now.` : 'None of yours are weak right now.',
      tint: 'amber',
    },
    {
      title: 'You get a fix for each',
      body: 'For every weak parameter you get what is going wrong, why, the exact steps to fix it, and your own calls where you already scored high on that same parameter.',
      note: 'Every fix links the calls it came from.',
      tint: 'primary',
    },
  ];

  return (
    <Panel className="overflow-hidden" data-testid="coaching-how-it-works">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-border">
        <SectionBadge icon={HelpCircle} tint="sky" size="sm" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">How this page works</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            It finds which scoring parameters you are weak in, and gives you a fix for each one.
          </p>
        </div>
        <button
          type="button"
          onClick={onHide}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors flex-shrink-0"
          aria-label="Hide this explanation"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid gap-px bg-border sm:grid-cols-3">
        {steps.map((step, index) => (
          <div key={step.title} className="bg-card p-4">
            <div className="flex items-center gap-2.5 mb-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold tabular-nums flex-shrink-0 ${TINTS[step.tint]}`}>
                {index + 1}
              </span>
              <p className="text-sm font-medium">{step.title}</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{step.body}</p>
            <p className="text-xs text-muted-foreground/70 leading-relaxed mt-1.5">{step.note}</p>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// One call where this person already scored well on the parameter being coached,
// with the reason the analyser gave for that score. This is the example to copy.
function ExemplarCall({ exemplar, max, onOpen }) {
  return (
    <li className="rounded-lg border border-teal-500/20 bg-white/[0.02] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums bg-teal-500/15 text-teal-300">
          {decimal1(exemplar.parameter_score)} / {max}
        </span>
        <span className="text-[11px] text-muted-foreground tabular-nums">{exemplar.date || '-'}</span>
        <button
          type="button"
          onClick={() => onOpen(exemplar.call_id)}
          className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
        >
          {exemplar.call_id}
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      {exemplar.why_this_score && (
        <p className="text-xs text-muted-foreground leading-relaxed mt-2">
          <span className="text-foreground">Why you scored this: </span>
          {exemplar.why_this_score}
        </p>
      )}
    </li>
  );
}

// One weak parameter and what to do about it. These behave as a single accordion
// — opening one closes the rest — because the advice is meant to be worked
// through one parameter at a time. Open/closed lives in the parent, not here:
// one card cannot know that another was just opened.
//
// Every section is data, not prose written on demand: `findings` are the
// analyser's own words on the calls that scored lowest, `tips` were written on
// those same calls when they were analysed, `exemplars` are the calls where this
// person already scored well, and `playbook` is the standing remedy.
function ParameterFix({ fix, rank, threshold, onOpen, open, onToggle }) {
  const average = fix.average ?? null;
  const shortBy = average === null ? null : Math.max(0, Math.round((threshold - average) * 10) / 10);
  const exemplars = fix.exemplars || [];
  const findings = (fix.findings || []).filter((f) => f.why_this_score);
  const tips = fix.tips || [];
  const playbook = fix.playbook;
  // Never call a 6.5 excellent: 'personal_best' means nothing on this parameter
  // reached the strong band, these are only the best this person has managed.
  const isStrongProof = exemplars.length > 0 && exemplars[0].tier === 'strong';
  const proofTitle = isStrongProof
    ? `You already do this well — your high ${fix.label.toLowerCase()} calls`
    : `Your best ${fix.label.toLowerCase()} calls so far`;
  const proofHint = isStrongProof
    ? 'You have scored well on this exact parameter. Copy what you did on these calls.'
    : 'Nothing hit the strong band here yet, but these are your best on this parameter — they are still the closest thing you have to copy.';

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* The heading wraps the button, not the other way round: that is the
          accessible accordion shape, and it keeps the parameter name a real
          heading for anyone navigating the page by headings. */}
      <h3 className="m-0">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={`w-full flex flex-wrap items-center gap-2.5 px-4 py-3.5 text-left bg-primary/[0.07] hover:bg-primary/[0.12] transition-colors ${open ? 'border-b-2 border-primary/25' : ''}`}
          data-testid={`coaching-fix-toggle-${fix.metric}`}
        >
          <span className="w-7 h-7 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center text-xs font-bold tabular-nums flex-shrink-0">
            {rank}
          </span>
          <span className="text-base font-bold tracking-tight">{fix.label}</span>
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${weakReasonOf(fix).chipClass}`}>
            {weakReasonOf(fix).chip}
          </span>
          <span className="text-[11px] tabular-nums text-muted-foreground ml-auto">
            scoring <span style={{ color: STATUS.weak.bar }}>{decimal1(average)}</span> / {fix.max}
            {shortBy !== null && shortBy > 0 && ` · ${decimal1(shortBy)} below the ${threshold} pass mark`}
          </span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </h3>

      {open && (
      <div className="p-4 sm:p-5 space-y-4">
        {/* The analyser's own words, on the calls where this parameter scored
            lowest. Quoted rather than summarised: this is exactly what the score
            was given for, so it cannot drift from the number beside it. */}
        {findings.length > 0 && (
          <Field icon={AlertTriangle} label={`What cost you marks on ${fix.label.toLowerCase()}`}>
            <ul className="space-y-2">
              {findings.map((finding) => (
                <li key={finding.call_id} className="rounded-lg border border-border/70 bg-white/[0.02] p-3">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums bg-amber-500/15 text-amber-300">
                      {decimal1(finding.parameter_score)} / {fix.max}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums">{finding.date || '-'}</span>
                    <button
                      type="button"
                      onClick={() => onOpen(finding.call_id)}
                      className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 border border-border text-[11px] text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                    >
                      {finding.call_id}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{finding.why_this_score}</p>
                </li>
              ))}
            </ul>
          </Field>
        )}

        {/* THE SOLUTION. Always present for every weak parameter, which is why
            it leads: the sections around it explain and personalise, but this is
            the part an agent can act on tomorrow morning. */}
        {playbook && (
          <>
            {playbook.do_this?.length > 0 && (
              <div className="rounded-lg border border-primary/25 bg-primary/[0.06] p-3">
                <Field icon={Target} label={`How to fix ${fix.label.toLowerCase()}`}>
                  {playbook.what_good_looks_like && (
                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                      <span className="text-foreground">What good looks like: </span>
                      {playbook.what_good_looks_like}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {playbook.do_this.map((step, index) => (
                      <li key={index} className="flex items-start gap-2 text-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ul>
                </Field>
              </div>
            )}

            {/* Tips the analyser wrote on this agent's own below-par calls for
                this parameter, filtered to the ones that are on-topic and ask
                for a change. Absent when none qualify — the fix above stands on
                its own, and padding it with off-topic advice helps nobody. */}
            {tips.length > 0 && (
              <Field icon={Wrench} label={`From your own ${fix.label.toLowerCase()} calls`}>
                <ul className="space-y-2">
                  {tips.map((tip) => (
                    <li key={tip.text} className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-300 mt-0.5 flex-shrink-0" />
                      <span className="leading-relaxed text-muted-foreground">
                        {tip.text}
                        {tip.calls > 1 && (
                          <span className="ml-1.5 text-[10px] text-amber-300 whitespace-nowrap">
                            came up on {tip.calls} calls
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </Field>
            )}

            <div className="grid gap-2.5 sm:grid-cols-2">
              {playbook.say_this_instead && (
                <div className="rounded-lg border border-border/70 bg-white/[0.02] p-3">
                  <Field icon={MessageSquareQuote} label="Say it like this">
                    <p className="text-xs italic leading-relaxed">"{playbook.say_this_instead}"</p>
                  </Field>
                </div>
              )}
              {playbook.practice_drill && (
                <div className="rounded-lg border border-border/70 bg-white/[0.02] p-3">
                  <Field icon={Dumbbell} label="Practise it">
                    <p className="text-xs text-muted-foreground leading-relaxed">{playbook.practice_drill}</p>
                  </Field>
                </div>
              )}
            </div>

            {playbook.how_to_measure && (
              <Field icon={Ruler} label="How you will know it worked">
                <p className="text-xs text-muted-foreground leading-relaxed">{playbook.how_to_measure}</p>
              </Field>
            )}
          </>
        )}

        {/* Proof, from the agent's own calls, that this parameter is not beyond
            them — and what they specifically did on the calls where it went
            right. Far more use than advice with no worked example behind it. */}
        {exemplars.length > 0 && (
          <div className="rounded-lg border border-teal-500/25 bg-teal-500/[0.05] p-3 space-y-3">
            <Field icon={Trophy} label={proofTitle}>
              <p className="text-xs leading-relaxed">{proofHint}</p>
            </Field>
            <ul className="space-y-2">
              {exemplars.map((exemplar) => (
                <ExemplarCall
                  key={exemplar.call_id}
                  exemplar={exemplar}
                  max={fix.max}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function CallRow({ call, rank, onOpen }) {
  return (
    <tr className="border-b border-border/60 last:border-0 hover:bg-white/5">
      <td className="py-2.5 px-3 text-xs text-muted-foreground tabular-nums">{rank}</td>
      <td className="py-2.5 px-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold tabular-nums ${scoreBadgeClass(call.score)}`}>
          {call.score ?? '-'}
        </span>
      </td>
      <td className="py-2.5 px-3 text-xs whitespace-nowrap">
        {call.date || '-'}
        {call.time && <span className="text-muted-foreground"> {call.time}</span>}
      </td>
      <td className="py-2.5 px-3 text-xs text-muted-foreground whitespace-nowrap">{formatDuration(call.duration_seconds)}</td>
      <td className="py-2.5 px-3 text-xs text-muted-foreground capitalize">{titleCase(call.call_purpose)}</td>
      <td className="py-2.5 px-3 text-xs text-muted-foreground capitalize">{titleCase(call.call_outcome)}</td>
      <td className="py-2.5 px-3 text-xs text-muted-foreground max-w-md">
        <span className="line-clamp-2">{call.summary || '-'}</span>
      </td>
      <td className="py-2.5 px-3 text-right">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => onOpen(call.call_id)}
          data-testid={`coaching-open-call-${call.call_id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          <span className="ml-1 hidden sm:inline">Open</span>
        </Button>
      </td>
    </tr>
  );
}

function CallTable({ calls, onOpen, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px]" data-testid={testId}>
        <thead>
          <tr className="border-b border-border text-left">
            {['#', 'Score', 'Date', 'Talk time', 'Purpose', 'Outcome', 'What happened', ''].map((heading, index) => (
              <th
                key={heading || `col-${index}`}
                className={`py-2 px-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground ${heading === '' ? 'text-right' : ''}`}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calls.map((call, index) => (
            <CallRow key={call.call_id} call={call} rank={index + 1} onOpen={onOpen} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AgentCoachingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState(() => ({ from_date: daysAgoInput(6), to_date: todayInput() }));
  const [activeTab, setActiveTab] = useState('plan');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(() => {
    try {
      return localStorage.getItem(HELP_HIDDEN_KEY) !== '1';
    } catch {
      return true;
    }
  });
  // Which parameter fix is expanded. One value for the whole set, so opening one
  // closes the others; null means every fix is collapsed.
  const [openFix, setOpenFix] = useState(null);

  const openCall = useCallback((callId) => {
    navigate(`/calls/${encodeURIComponent(callId)}`);
  }, [navigate]);

  const hideHelp = useCallback(() => {
    setShowHelp(false);
    try { localStorage.setItem(HELP_HIDDEN_KEY, '1'); } catch { /* private mode - fine */ }
  }, []);

  const toggleHelp = useCallback(() => {
    setShowHelp((prev) => {
      const next = !prev;
      try {
        if (next) localStorage.removeItem(HELP_HIDDEN_KEY);
        else localStorage.setItem(HELP_HIDDEN_KEY, '1');
      } catch { /* private mode - fine */ }
      return next;
    });
  }, []);

  // One request builds the whole screen. There is no generate step and no cache
  // to warm: the response is derived from stored analysis, so it is already
  // current the moment it arrives.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setOverview(null);

    api.getCoachingOverview(dateRange.from_date, dateRange.to_date)
      .then((res) => { if (!cancelled) setOverview(res); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load coaching data'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [dateRange.from_date, dateRange.to_date]);

  const summary = overview?.summary;
  const parameters = useMemo(() => overview?.parameters || [], [overview]);
  // The weak parameters ARE the fixes now — each one arrives with its findings,
  // tips, exemplars and playbook attached. There is no second list to reconcile
  // against, so a parameter can no longer appear in one and be missing from the
  // other, which is what every "no usable fix came back" complaint came down to.
  const weakParameters = useMemo(() => overview?.weak_parameters || [], [overview]);
  const lowCalls = overview?.low_calls || [];

  // Open at number one — the panel tells people to start there. Keyed off the
  // data so changing period resets the accordion rather than leaving a parameter
  // from the previous period expanded.
  useEffect(() => {
    setOpenFix(weakParameters[0]?.metric ?? null);
  }, [weakParameters]);

  const blocked = Boolean(overview?.insufficient_data_reason);
  const threshold = summary?.weak_threshold ?? 7;

  const totalParameters = parameters.length;
  // Only parameters with a score feed the personal average, so they are what the
  // "you average X across N" copy must count.
  const scoredParameters = parameters.filter((p) => p.average !== null && p.average !== undefined).length;
  const evidenceCallCount = useMemo(
    () => new Set(weakParameters.flatMap((p) => p.evidence_call_ids || [])).size,
    [weakParameters]
  );

  // Two different things were being counted as if they were one, which is why
  // the numbers looked broken: a parameter's BAND (weak / OK / strong, from its
  // score) and whether it is BEING COACHED (a pick of the three lowest). They
  // overlap but never had to add up — "3 coached" and "4 strong" only summed to
  // 7 by luck. Bands are what partition the seven, so bands are what the strip
  // now reports, with the coached count shown separately as the selection it is.
  const bandCounts = useMemo(() => {
    const counts = { weak: 0, moderate: 0, strong: 0, unscored: 0 };
    parameters.forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });
    return counts;
  }, [parameters]);

  const bandSummary = [
    bandCounts.strong ? `${bandCounts.strong} strong` : null,
    bandCounts.moderate ? `${bandCounts.moderate} OK` : null,
    bandCounts.weak ? `${bandCounts.weak} below the pass mark` : null,
    bandCounts.unscored ? `${bandCounts.unscored} not scored` : null,
  ].filter(Boolean).join(' · ');

  const tabCounts = { plan: weakParameters.length, practice: lowCalls.length };

  return (
    <div className="space-y-4" data-testid="agent-coaching-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight leading-tight">My Coaching</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {overview?.agent?.agent_name || user?.name} · the scoring parameters you are weak in, and how to fix each one
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 px-2.5"
            onClick={toggleHelp}
            aria-pressed={showHelp}
            title={showHelp ? 'Hide the explanation' : 'What is this page?'}
            data-testid="coaching-help-toggle"
          >
            <HelpCircle className="w-4 h-4" />
            <span className="ml-1.5 hidden sm:inline">What is this?</span>
          </Button>
          <DateRangePicker
            value={dateRange}
            presets={[
              { label: 'Last 7 days', from_date: daysAgoInput(6), to_date: todayInput() },
              { label: 'Last 30 days', from_date: daysAgoInput(29), to_date: todayInput() },
              { label: '3 Months', from_date: monthsAgoInput(3), to_date: todayInput() },
              { label: '6 Months', from_date: monthsAgoInput(6), to_date: todayInput() },
            ]}
            onChange={(range) => {
              if (!range.from_date || !range.to_date) return;
              setDateRange({ from_date: range.from_date, to_date: range.to_date });
            }}
            onClear={() => setDateRange({ from_date: daysAgoInput(6), to_date: todayInput() })}
            buttonClassName="h-9"
            data-testid="coaching-date-range"
          />
        </div>
      </div>

      {loading ? (
        <Panel>
          <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading your scores...
          </div>
        </Panel>
      ) : !overview ? (
        <Panel>
          <EmptyState
            icon={Info}
            title="Could not load your coaching data"
            description="Try a different date range, or refresh the page."
          />
        </Panel>
      ) : (
        <>
          {showHelp && (
            <HowItWorks onHide={hideHelp} weakCount={weakParameters.length} threshold={threshold} />
          )}

          {/* Stat strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-xl overflow-hidden">
            <Stat
              label="Calls scored by AI"
              value={summary.scored_calls}
              hint={`out of ${summary.total_calls} calls you took`}
            />
            <Stat
              label="Your overall score"
              value={summary.avg_score ?? '-'}
              hint="average of all scored calls, out of 100"
            />
            {/* These two count different things on purpose. The first splits all
                seven parameters by score, so it always adds up to seven. The
                second is how many of them have a fix below — a selection of the
                lowest few, which is why it is not part of the split. */}
            <Stat
              label="Parameters scored"
              value={totalParameters}
              hint={bandSummary || 'none scored yet'}
            />
            <Stat
              label="Being coached"
              value={weakParameters.length}
              hint={weakParameters.length > 0 ? 'your lowest — each has a fix below' : 'nothing needs coaching'}
              accent={weakParameters.length > 0 ? 'text-amber-300' : 'text-teal-300'}
            />
          </div>

          {blocked && (
            <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3">
              <Info className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-200">Nothing to fix from this period</p>
                <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">{overview.insufficient_data_reason}</p>
              </div>
            </div>
          )}

          {/* Tab bar */}
          <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === key
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`coaching-tab-${key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {tabCounts[key] > 0 && (
                  <span className="text-[11px] text-muted-foreground tabular-nums">{tabCounts[key]}</span>
                )}
              </button>
            ))}
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Tab: My Weak Areas                                               */}
          {/* ---------------------------------------------------------------- */}
          {activeTab === 'plan' && (
            <div className="space-y-4" data-testid="coaching-tab-panel-plan">
              {/* Weak areas, in words. No chart: the question is "what am I weak
                  at and what do I do about it", and a bar answers neither. */}
              <Panel>
                <PanelHeader
                  icon={Target}
                  tint="amber"
                  // Only say "weak" when something actually is. For Binet all
                  // three coached parameters clear the pass mark — calling that
                  // "you are weak in 3 of 7" is simply untrue, and it is the
                  // wording people quote back when they lose faith in a report.
                  title={
                    weakParameters.length === 0
                      ? 'You are not weak in any scored parameter'
                      : bandCounts.weak > 0
                        ? `${bandCounts.weak} of your ${totalParameters} parameters ${bandCounts.weak === 1 ? 'is' : 'are'} below the pass mark`
                        : `Your ${weakParameters.length} lowest of ${totalParameters} parameters — all above the pass mark`
                  }
                  description={
                    bandCounts.weak > 0
                      ? `Each call is scored out of 10 on ${totalParameters} parameters. Anything averaging below ${threshold} is weak. The ${weakParameters.length} lowest are coached below.`
                      : `Each call is scored out of 10 on ${totalParameters} parameters, and none of yours average below ${threshold}. These are simply your lowest, so they are where the most room is left.`
                  }
                />
                {weakParameters.length === 0 ? (
                  <EmptyState
                    icon={CheckCircle2}
                    title="Nothing is below the pass mark"
                    description={`Every parameter is averaging ${threshold} or above in this period.`}
                  />
                ) : (
                  <div>
                    {weakParameters.map((item, index) => (
                      <div
                        key={item.metric}
                        className="px-4 sm:px-5 py-4 border-b border-border/60 last:border-0"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-primary/20 border border-primary/40 text-primary flex items-center justify-center text-[11px] font-bold tabular-nums flex-shrink-0">
                            {index + 1}
                          </span>
                          <h3 className="text-[15px] font-bold tracking-tight">{item.label}</h3>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${weakReasonOf(item).chipClass}`}>
                            {weakReasonOf(item).chip}
                          </span>
                          <span className="text-xs text-muted-foreground ml-auto tabular-nums whitespace-nowrap">
                            you average <span style={{ color: STATUS.weak.bar }}>{decimal1(item.average)}</span> / {item.max}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                          <span className="text-foreground">Why it is flagged: </span>
                          {weakReasonOf(item).explain(item, threshold, scoredParameters)}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                          <span className="text-foreground">What this measures: </span>
                          {PARAMETER_MEANING[item.metric] || 'Part of the call quality score.'}
                        </p>
                        {item.exemplars?.length > 0 && (
                          <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                            <span className="text-teal-300">You have already done it: </span>
                            you scored{' '}
                            <span className="tabular-nums">
                              {decimal1(Math.max(...item.exemplars.map((e) => e.parameter_score)))} / {item.max}
                            </span>{' '}
                            on {item.exemplars.length} call{item.exemplars.length === 1 ? '' : 's'} in this
                            period — those calls are shown with the fix below.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {/* Per-parameter fixes. Nothing to generate and nothing to wait
                  for — they arrive with the overview. */}
              {blocked ? (
                <Panel>
                  <EmptyState
                    icon={CheckCircle2}
                    title="No weak parameters in this period"
                    description={overview.insufficient_data_reason}
                  />
                </Panel>
              ) : (
                <>
                  {weakParameters.length > 0 && (
                    <section>
                      <SectionHeading
                        icon={Wrench}
                        tint="amber"
                        title="How to fix each weak parameter"
                        description="One per weak parameter, worst first — what cost you marks, what was suggested at the time, how to fix it, and your own best calls on it. Start at number one; click any heading to open or close it."
                        count={weakParameters.length}
                      />
                      <div className="space-y-3">
                        {weakParameters.map((fix, index) => (
                          <ParameterFix
                            key={fix.metric}
                            fix={fix}
                            rank={index + 1}
                            threshold={threshold}
                            onOpen={openCall}
                            open={openFix === fix.metric}
                            onToggle={() => setOpenFix((prev) => (prev === fix.metric ? null : fix.metric))}
                          />
                        ))}
                      </div>
                    </section>
                  )}

                  {parameters.length > 0 && (
                    <Collapsible
                      icon={ClipboardList}
                      tint="sky"
                      title={`Your score on all ${totalParameters} parameters`}
                      subtitle="Every parameter, and whether it is being coached this period"
                      count={parameters.length}
                    >
                      <div>
                        {parameters.map((item) => {
                          const band = STATUS[item.status] || STATUS.unscored;
                          const explainNotCoached = NOT_COACHED_REASON[item.not_coached_reason];
                          return (
                            <div
                              key={item.key}
                              className="flex items-start justify-between gap-3 py-2.5 border-b border-border/60 last:border-0"
                            >
                              <div className="min-w-0">
                                <p className="text-sm">{item.label}</p>
                                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                                  {PARAMETER_MEANING[item.key]}
                                </p>
                                {/* Say why, always. A parameter that is neither
                                    coached nor explained just looks missing. */}
                                {item.coached ? (
                                  <p className="text-[11px] text-primary leading-relaxed mt-1">
                                    Coached below — this is one of your lowest, so it has a fix.
                                  </p>
                                ) : explainNotCoached && (
                                  <p className="text-[11px] text-muted-foreground/80 leading-relaxed mt-1">
                                    {explainNotCoached(item.max)}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs tabular-nums">{decimal1(item.average)} / {item.max}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${band.chip}`}>
                                  {band.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Collapsible>
                  )}

                  <p className="text-[11px] text-muted-foreground leading-relaxed px-1 pt-2 border-t border-border">
                    {weakParameters.length} weak parameter
                    {weakParameters.length === 1 ? '' : 's'}, from{' '}
                    {evidenceCallCount} of your lowest-scoring calls on those parameters,
                    compared against your own highest-scoring calls on the same parameters. Which
                    parameters count as weak is calculated from your scores, and everything quoted
                    here was written when each call was analysed — nothing on this page is
                    generated when you open it.
                  </p>
                </>
              )}
            </div>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Tab: Calls To Fix                                                */}
          {/* ---------------------------------------------------------------- */}
          {activeTab === 'practice' && (
            <Panel data-testid="coaching-tab-panel-practice">
              <PanelHeader
                icon={ClipboardList}
                tint="amber"
                title="Your lowest-scoring calls"
                description="Where the weak parameters show up most. Listen to one of these next to a top call and the difference is usually obvious."
              />
              {lowCalls.length === 0 ? (
                <EmptyState
                  icon={ClipboardList}
                  title="No analysed calls in this period"
                  description="Once your calls have been processed they will appear here."
                />
              ) : (
                <CallTable calls={lowCalls} onOpen={openCall} testId="coaching-low-calls-table" />
              )}
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
